/**
 * Recursive table traversal utilities using contextPath
 *
 * These utilities provide a clean way to navigate and update deeply nested
 * table structures (subtables within nested tables within subtables, etc.)
 * using the contextPath system.
 */

/**
 * Recursively traverse table structure and apply update function at the target location
 *
 * This is the core traversal function that all other update operations use.
 * It follows a contextPath through the nested structure and applies an update
 * function when it reaches the target.
 *
 * @param {Object} table - The root table object
 * @param {Object} contextPath - ContextPath object { tableId, path: [{type, id}, ...] }
 * @param {Function} updateFn - Function to apply at target (receives current structure, returns updated structure)
 * @returns {Object} - Updated table (new object, original unchanged)
 *
 * @example
 * const updated = traverseAndUpdate(table, contextPath, (structure) => ({
 *   ...structure,
 *   name: 'New Name'
 * }));
 */
export const traverseAndUpdate = (table, contextPath, updateFn) => {
  // Safety check: ensure we're operating on the correct table
  if (table.id !== contextPath.tableId) {
    console.warn('[traverseAndUpdate] Table ID mismatch:', table.id, 'vs', contextPath.tableId);
    return table;
  }

  /**
   * Recursive helper that walks the path
   * @param {Object} currentStructure - Current table/subtable/nested structure
   * @param {number} pathIndex - Which segment of the path we're at
   * @returns {Object} - Updated structure
   */
  const traverse = (currentStructure, pathIndex) => {
    // Base case: we've reached the target depth, apply the update
    if (pathIndex >= contextPath.path.length) {
      return updateFn(currentStructure);
    }

    const segment = contextPath.path[pathIndex];
    const updated = { ...currentStructure };

    if (segment.type === 'subtable') {
      // Navigate into subtables
      if (updated.row_subtables) {
        updated.row_subtables = { ...updated.row_subtables };

        // Search through all rows for the subtable
        for (const rowKey in updated.row_subtables) {
          // Always create new array to ensure React detects change
          updated.row_subtables[rowKey] = updated.row_subtables[rowKey].map(subtable => {
            if (subtable.id === segment.id) {
              // Found it! Recurse deeper
              return traverse(subtable, pathIndex + 1);
            }
            return subtable;
          });
        }
      }
    } else if (segment.type === 'nested') {
      // Navigate into nested tables (they're cells in the rows)
      if (updated.rows) {
        updated.rows = updated.rows.map(row =>
          row.map(cell => {
            if (cell && typeof cell === 'object' && cell.type === 'nested_table' && cell.id === segment.id) {
              // Found it! Recurse deeper
              return traverse(cell, pathIndex + 1);
            }
            return cell;
          })
        );
      }
    }

    return updated;
  };

  return traverse(table, 0);
};

/**
 * Update column header at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @param {number} colIndex - Column index to update
 * @param {string} label - New header label
 * @returns {Object} - Updated structure
 */
export const updateColumnHeaderAtPath = (structure, colIndex, label) => {
  const updated = { ...structure };

  // Ensure column_headers array exists
  if (!updated.column_headers) {
    updated.column_headers = Array(updated.num_cols || 0).fill(null).map((_, i) => `Column ${i + 1}`);
  }

  // Create new array and update the header
  updated.column_headers = [...updated.column_headers];
  updated.column_headers[colIndex] = label;

  return updated;
};

/**
 * Update row header at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @param {number} rowIndex - Row index to update
 * @param {string} label - New header label
 * @returns {Object} - Updated structure
 */
export const updateRowHeaderAtPath = (structure, rowIndex, label) => {
  const updated = { ...structure };

  // Ensure row_headers array exists
  if (!updated.row_headers) {
    updated.row_headers = Array(updated.num_rows || 0).fill(null).map((_, i) => `Row ${i + 1}`);
  }

  // Create new array and update the header
  updated.row_headers = [...updated.row_headers];
  updated.row_headers[rowIndex] = label;

  return updated;
};

/**
 * Update name at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @param {string} name - New name
 * @returns {Object} - Updated structure
 */
export const updateNameAtPath = (structure, name) => {
  return { ...structure, name };
};

/**
 * Toggle expanded state at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @returns {Object} - Updated structure with toggled expanded state
 */
export const toggleExpandedAtPath = (structure) => {
  return { ...structure, expanded: !structure.expanded };
};

/**
 * Toggle column headers visibility at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @returns {Object} - Updated structure with toggled show_headers
 */
export const toggleShowHeadersAtPath = (structure) => {
  const updated = {
    ...structure,
    show_headers: !structure.show_headers
  };

  // Initialize column_headers if toggling on and they don't exist
  if (updated.show_headers && !updated.column_headers) {
    updated.column_headers = Array(updated.num_cols || 0).fill(null).map((_, i) => `Column ${i + 1}`);
  }

  return updated;
};

/**
 * Toggle row headers visibility at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @returns {Object} - Updated structure with toggled show_row_headers
 */
export const toggleShowRowHeadersAtPath = (structure) => {
  const updated = {
    ...structure,
    show_row_headers: !structure.show_row_headers
  };

  // Initialize row_headers if toggling on and they don't exist
  if (updated.show_row_headers && !updated.row_headers) {
    updated.row_headers = Array(updated.num_rows || 0).fill(null).map((_, i) => `Row ${i + 1}`);
  }

  return updated;
};

/**
 * Insert row at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @param {number} position - Row position (0-based index)
 * @param {string} direction - 'above' or 'below'
 * @returns {Object} - Updated structure with new row
 */
export const insertRowAtPath = (structure, position, direction) => {
  const updated = { ...structure };
  const numCols = updated.num_cols || (updated.rows && updated.rows[0] ? updated.rows[0].length : 2);
  const newRow = Array(numCols).fill('');

  updated.rows = [...updated.rows];
  const insertIndex = direction === 'above' ? position : position + 1;
  updated.rows.splice(insertIndex, 0, newRow);

  updated.num_rows = updated.rows.length;

  // Update row headers if they exist
  if (updated.row_headers) {
    updated.row_headers = [...updated.row_headers];
    updated.row_headers.splice(insertIndex, 0, `Row ${insertIndex + 1}`);
  }

  return updated;
};

/**
 * Insert column at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @param {number} position - Column position (0-based index)
 * @param {string} direction - 'left' or 'right'
 * @returns {Object} - Updated structure with new column
 */
export const insertColumnAtPath = (structure, position, direction) => {
  const updated = { ...structure };
  const insertIndex = direction === 'left' ? position : position + 1;

  // Insert empty cell into each row
  updated.rows = updated.rows.map(row => {
    const newRow = [...row];
    newRow.splice(insertIndex, 0, '');
    return newRow;
  });

  updated.num_cols = updated.rows[0]?.length || 0;

  // Update column headers if they exist
  if (updated.column_headers) {
    updated.column_headers = [...updated.column_headers];
    updated.column_headers.splice(insertIndex, 0, `Column ${insertIndex + 1}`);
  }

  return updated;
};

/**
 * Delete row at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @param {number} position - Row position to delete (0-based index)
 * @returns {Object} - Updated structure with row removed
 */
export const deleteRowAtPath = (structure, position) => {
  const updated = { ...structure };

  updated.rows = [...updated.rows];
  updated.rows.splice(position, 1);

  updated.num_rows = updated.rows.length;

  // Update row headers if they exist
  if (updated.row_headers) {
    updated.row_headers = [...updated.row_headers];
    updated.row_headers.splice(position, 1);
  }

  return updated;
};

/**
 * Delete column at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @param {number} position - Column position to delete (0-based index)
 * @returns {Object} - Updated structure with column removed
 */
export const deleteColumnAtPath = (structure, position) => {
  const updated = { ...structure };

  // Remove cell from each row
  updated.rows = updated.rows.map(row => {
    const newRow = [...row];
    newRow.splice(position, 1);
    return newRow;
  });

  updated.num_cols = updated.rows[0]?.length || 0;

  // Update column headers if they exist
  if (updated.column_headers) {
    updated.column_headers = [...updated.column_headers];
    updated.column_headers.splice(position, 1);
  }

  return updated;
};

/**
 * Move row up or down at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @param {number} fromIndex - Current row index
 * @param {string} direction - 'up' or 'down'
 * @returns {Object} - Updated structure with row moved
 */
export const moveRowAtPath = (structure, fromIndex, direction) => {
  const updated = { ...structure };
  const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;

  // Bounds check
  if (toIndex < 0 || toIndex >= updated.rows.length) {
    return updated;
  }

  // Swap rows
  updated.rows = [...updated.rows];
  [updated.rows[fromIndex], updated.rows[toIndex]] = [updated.rows[toIndex], updated.rows[fromIndex]];

  // Swap row headers if they exist
  if (updated.row_headers) {
    updated.row_headers = [...updated.row_headers];
    [updated.row_headers[fromIndex], updated.row_headers[toIndex]] =
      [updated.row_headers[toIndex], updated.row_headers[fromIndex]];
  }

  return updated;
};

/**
 * Move column left or right at contextPath location
 *
 * @param {Object} structure - The table/subtable/nested structure
 * @param {number} fromIndex - Current column index
 * @param {string} direction - 'left' or 'right'
 * @returns {Object} - Updated structure with column moved
 */
export const moveColumnAtPath = (structure, fromIndex, direction) => {
  const updated = { ...structure };
  const toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;

  // Bounds check
  const numCols = updated.rows[0]?.length || 0;
  if (toIndex < 0 || toIndex >= numCols) {
    return updated;
  }

  // Swap cells in each row
  updated.rows = updated.rows.map(row => {
    const newRow = [...row];
    [newRow[fromIndex], newRow[toIndex]] = [newRow[toIndex], newRow[fromIndex]];
    return newRow;
  });

  // Swap column headers if they exist
  if (updated.column_headers) {
    updated.column_headers = [...updated.column_headers];
    [updated.column_headers[fromIndex], updated.column_headers[toIndex]] =
      [updated.column_headers[toIndex], updated.column_headers[fromIndex]];
  }

  return updated;
};
